import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CatalogsService } from '../../../services/catalogs.services';
import { Subject, takeUntil, distinctUntilChanged, filter, Observable, of, Subscription, tap, skip, combineLatest, catchError } from 'rxjs';
import { INewCatalog } from '../new-catalog.component';
import { IProduct, IProductCurrency } from 'src/app/features/produits/models/products.models';
import { IGeneralRules, IHandlingUnitType } from '../../../models/catalogs.models';
import { customValidator } from 'src/app/shared/validators/fields-validators';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter } from '@angular/material/core';
import { ICurrency, ICustomer, ILocation } from 'src/app/features/customers/models/customers.models';
import { Store } from '@ngrx/store';
import { FetchAllCurrencies } from 'src/app/features/customers/store/customers.actions';
import { AppState } from 'src/app/core/app.state';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { ISubsidiary } from 'src/app/features/subsidiaries/models/subsidiary.model';
import { SubsidiaryService } from 'src/app/features/subsidiaries/services/subsidiary.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { IAgent } from 'src/app/features/agents/models/agents.models';
import { GetAgents } from 'src/app/features/agents/store/agents.actions';
import { SubsidiaryChangeConfirmationDialogComponent } from '../subsidiary-change-confirmation-dialog/subsidiary-change-confirmation-dialog.component';
import { ICustomField, IObjectCustomFields, IObjectCustomFieldsParams } from 'src/app/features/settings/models/settings.models';
import { CustomFieldVisibilityTypes, RelatedObjectsTypes } from 'src/app/features/settings/models/custom-fields.constants';
import { LanguageService } from 'src/app/config/language.service';
import { FetchCustomFieldsPage, GetObjectCustomFields, UpdateObjectCustomFields } from 'src/app/features/settings/store/settings.actions';
import { CatalogCustomFieldsComponent } from '../../catalog-custom-fields/catalog-custom-fields.component';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
import { HasPermissionContext } from 'src/app/shared/has-permission.directive';

@Component({
  selector: 'app-new-catalog-settings',
  templateUrl: './new-catalog-settings.component.html',
  styleUrls: ['./new-catalog-settings.component.scss'],
})
export class NewCatalogSettingsComponent implements OnInit {

  currentLocale: string = this.translateService.currentLang || 'en';
  
  @Input() catalogType: 'agents' | 'catalogs' = 'catalogs';
  @Output() isAllFormsValid = new EventEmitter<boolean>(true);
  basicSettingsForm!: FormGroup;
  pricingRulesForm!: FormGroup;
  currenciesForm!: FormGroup;
  orderRulesForm!: FormGroup;
  orderAmountForm!: FormGroup;
  quantityRulesForm!: FormGroup;
  imagePreview: string | null = null;
  imageLoadError: boolean = false;
  hasExtraDiscount: boolean = false;
  isMultiCurrency: boolean = false;
  enabledHandlingUnits: number[] = [];
  defaultHandlingUnit: number | null = null;
  availableHandlingUnits: IHandlingUnitType[] = [];
  pricingRules: any[] = [
    {
      name: 'Catalogs.new_catalog.settings.agent_pricing.pricing_rule.discount',
      description: 'Discount on MSRP',
      value: 'discount',
    },
    {
      name: 'Catalogs.new_catalog.settings.agent_pricing.pricing_rule.margin',
      description: 'Margin on top of Cost',
      value: 'margin',
    },
  ];

  defaultCatalogData!: INewCatalog;

  destroy$ = new Subject<void>()
  currencies$: Observable<ICurrency[]> = of([]);
  allCurrencies: ICurrency[] = [];
  defaultCurrency: ICurrency | undefined = LocalStorageService.get('currentUser')?.user?.currency!;

  subsidiariesLoading: boolean = false;
  allSubsidiaries: ISubsidiary[] = [];
  selectedSubsidiary: ISubsidiary | undefined = undefined;
  private previousSubsidaryId: number | null = null;
  private isSubsidaryChangeInProgress: boolean = false;

  @ViewChild('subsidarySelect') subsidarySelect!: MatSelect;
  objectCustomField$: Observable<IObjectCustomFields | null> = this.store.select(state => state.settings.objectCustomFields);
  customFields$: Observable<ICustomField[]> = this.store.select((state) => state.settings.customFields);
  objectCustomFields: IObjectCustomFields | null = null;
  objectCustomFieldsParams: IObjectCustomFieldsParams = {
    object_type: RelatedObjectsTypes.CATALOG,
    record_id: 0,
    visibility: [CustomFieldVisibilityTypes.BACKOFFICE],
    include_definitions: true,
  };
  currentLanguage: string = this.languageService.getCurrentLanguage();
  canCreate = false;
  canEdit = false;
  constructor(
    private catalogService: CatalogsService,
    private subsidiaryService: SubsidiaryService,
    private translateService: TranslateService,
    private dateAdapter: DateAdapter<Date>,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private languageService: LanguageService,
  ) {}

  get selectedCurrencyName(): string {
    const id = this.currenciesForm?.get('default_currency')?.value;
    const match = this.allCurrencies.find((c) => c.id === id);
    return match ? match.name + ' (' + match.symbol + ')' : '';
  }

  get selectedPricingRuleName(): string {
    const value = this.pricingRulesForm?.get('pricing_rule')?.value;
    const match = this.availablePricingRules.find((r) => r.value === value);
    return match ? this.translateService.instant(match.name) : '';
  }

  get availablePricingRules(): any[] {
    // Show all pricing rules for both agents and customers
    return this.pricingRules;
  }

  get isPricingRuleDisabled(): boolean {
    const status = this.defaultCatalogData?.status;
    // Disable only if status is 'Approved'
    return status === 'Approved';
  }

  get selectedEnabledCurrencies(): ICurrency[] {
    const ids: Array<string | number> = this.currenciesForm?.get('enabled_currencies')?.value || [];
    if (!Array.isArray(ids) || ids.length === 0) return [];
    const stringIds = ids.map((v) => String(v));
    return this.allCurrencies.filter((c) => stringIds.includes(String(c.id)));
  }

  ngOnInit(): void {
    this.dateAdapter.setLocale(`${this.currentLocale.toLocaleLowerCase()}-${this.currentLocale.toUpperCase()}`);
    this.store.dispatch(FetchAllCurrencies());
    this.store.dispatch(FetchCustomFieldsPage({ object_type: RelatedObjectsTypes.CATALOG, append: false }))
    
    this.currencies$ = this.store.select((state) => state.customers.allCurrencies);
    this.currencies$.pipe(takeUntil(this.destroy$)).subscribe((list) => this.allCurrencies = list || []);
    
    this.loadSubsidaries();

    this.initBasicSettingsForm();
    this.initPricingRulesForm();
    this.initCurrenciesForm();
    this.initOrderAmountForm();
    this.initQuantityRulesForm();
    this.store.select(selectCanDo('catalog', 'create')).pipe(takeUntil(this.destroy$)).subscribe((canCreate) => {
      this.canCreate = canCreate;
      this.applyReadOnlyState();
    });
    this.store.select(selectCanDo('catalog', 'edit')).pipe(takeUntil(this.destroy$)).subscribe((canEdit) => {
      this.canEdit = canEdit;
      this.applyReadOnlyState();
    });

    const handlingUnits$ = this.catalogService.getAvailableHandlingUnits().pipe(
      takeUntil(this.destroy$),
      catchError((error) => {
        this.availableHandlingUnits = [];
        return of({ data: [] } as any);
      })
    );

    const catalogData$ = this.catalogService.newCatalogData$.pipe(
      tap((data) => {
        const catalogId = data?.catalogtId;
        this.objectCustomFields = null;
        if(catalogId) {
          const oldCatalogId = this.objectCustomFieldsParams.record_id;
          if (oldCatalogId !== catalogId) {
            this.objectCustomFieldsParams = {
              ...this.objectCustomFieldsParams,
              record_id: catalogId,
            };
            this.store.dispatch(GetObjectCustomFields({ params: this.objectCustomFieldsParams }));
          }
          this.objectCustomField$.pipe(takeUntil(this.destroy$)).subscribe((objectCustomFields) => {
            this.objectCustomFields = objectCustomFields;
          });
        } else {
          this.customFields$.pipe(takeUntil(this.destroy$)).subscribe((customFields) => {
            this.objectCustomFields = {
              values: data?.custom_fields ?? this.objectCustomFields?.values ?? {},
              definitions: customFields,
            } as IObjectCustomFields;
          });
        }
      }),
      takeUntil(this.destroy$),
      filter((d): d is INewCatalog => !!d),
      distinctUntilChanged((prev, curr) => {
        const prevSnap = this.buildComparableCatalogSnapshot(prev);
        const currSnap = this.buildComparableCatalogSnapshot(curr);
        return JSON.stringify(prevSnap) === JSON.stringify(currSnap);
      })
    );

    combineLatest([handlingUnits$, catalogData$]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([handlingUnitsResponse, data]) => {
      this.enabledHandlingUnits = [];
      this.defaultHandlingUnit = null;
      
      this.availableHandlingUnits = Array.isArray(handlingUnitsResponse.data) 
        ? handlingUnitsResponse.data 
        : (handlingUnitsResponse.data ? [handlingUnitsResponse.data] : []);

      this.defaultCatalogData = data;

        this.hasExtraDiscount = !!data.has_extra_discount;
        this.isMultiCurrency = data.is_multi_currency ?? false;

        // This is used when editing an existing catalog, this just incase the subsidiaries API call happens after the getCatalogById call.
        if(data.subsidiary_id) {
          this.selectedSubsidiary = this.allSubsidiaries.find(s => s.id === data.subsidiary_id);
        }

        // This is used when editing an existing catalog, this just incase the subsidiaries API call happens after the getCatalogById call.
        if(data.subsidiary_id) {
          this.selectedSubsidiary = this.allSubsidiaries.find(s => s.id === data.subsidiary_id);
        }

      // Handle image preview and file control
      const file = data.cover;
      if (file && file.size > 0) {
        const reader = new FileReader();
        reader.onload = () => {
          this.imagePreview = reader.result as string;
          this.imageLoadError = false; // Reset error flag
          this.basicSettingsForm.get('cover')?.setValue(file);
        };
        reader.readAsDataURL(file);
      } else if (data.coverUrl) {
        this.imagePreview = data.coverUrl;
        this.imageLoadError = false; // Reset error flag
      } else {
        this.imagePreview = null;
        this.imageLoadError = false; // Reset error flag
      }
            // Patch all forms with incoming values
      this.basicSettingsForm.patchValue({
          subsidiary_id: data.subsidiary_id || null,
        name: data.name || '',
        description: data.description || '',
        expiration_date: data.expiration_date || '',
        hasExpirationDate: data.expiration_date !== null && data.expiration_date !== '' ? true : false,
      }, { emitEvent: false });

      // Update pricing rule disabled state based on status only
      const isDisabled = data.status === 'Approved';
      if (isDisabled) {
        this.pricingRulesForm.get('pricing_rule')?.disable({ emitEvent: false });
      } else {
        this.pricingRulesForm.get('pricing_rule')?.enable({ emitEvent: false });
      }

      const pricingRule = data.pricing_rule || 'discount';

      this.pricingRulesForm.patchValue({
        pricing_rule: pricingRule,
        overall_pricing_value: data.overall_pricing_value ?? 0,
        extra_discount: data.extra_discount ?? null,
        extra_discount_blocking: data.extra_discount_blocking ?? false,
      }, { emitEvent: false });
            this.currenciesForm.patchValue({          
        default_currency: (data as any).default_currency ?? this.defaultCurrency?.id ?? null,
        enabled_currencies: ((data as any).enabled_currencies as string[]) ?? [],
        is_multi_currency: data.is_multi_currency ?? false,
        show_full_pricing_data: (data as any).show_full_pricing_data ?? true,
      }, { emitEvent: false });

      this.orderAmountForm.patchValue({
        min_order_amount: data.min_order_amount ?? null,
      }, { emitEvent: false });

        this.quantityRulesForm.patchValue({
          increment: data.quantity_rules?.increment ?? 1,
          max_qte: data.quantity_rules?.max_qte ?? null,
          min_qte: data.quantity_rules?.min_qte ?? 1,
          can_be_sold_out_of_stock: data.quantity_rules?.can_be_sold_out_of_stock ?? false,
          show_stock: data.quantity_rules?.show_stock ?? true,
        }, { emitEvent: false });

        // Emit combined form validity after patching all forms with existing catalog data
        const allFormsValid = this.basicSettingsForm.valid && 
                              this.pricingRulesForm.valid && 
                              this.currenciesForm.valid && 
                              this.quantityRulesForm.valid;
        this.isAllFormsValid.emit(allFormsValid);

        const enabledHandlingUnitIds = (data as any).enabled_handling_unit_ids ?? [];
        const defaultHandlingUnitId = (data as any).default_handling_unit_id ?? null;

        if (enabledHandlingUnitIds.length === 0 && this.availableHandlingUnits.length > 0) {
          const allUnitIds = this.availableHandlingUnits.map(hu => hu.id);
          const firstUnitId = this.availableHandlingUnits[0].id;
          this.enabledHandlingUnits = allUnitIds;
          this.defaultHandlingUnit = firstUnitId;
          
          this.catalogService.setNewCatalogData({
            enabled_handling_unit_ids: allUnitIds,
            default_handling_unit_id: firstUnitId,
          } as any);
        } else {
          this.enabledHandlingUnits = enabledHandlingUnitIds;
          this.defaultHandlingUnit = defaultHandlingUnitId;
          
          if (enabledHandlingUnitIds.length > 0 || defaultHandlingUnitId !== null) {
            this.catalogService.setNewCatalogData({
              enabled_handling_unit_ids: enabledHandlingUnitIds,
              default_handling_unit_id: defaultHandlingUnitId,
            } as any);
          }
        }
        this.applyReadOnlyState();
      });
  }

  loadSubsidaries(): void {
    // Load subsidiaries
    this.subsidiariesLoading = true;
    this.subsidiaryService.getAll().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (subsidaries) => {
        this.allSubsidiaries = subsidaries.data || [];
        this.subsidiariesLoading = false;

        // This is used when creating a new catalog
        // if the catalog has no subsidiary_id, set the main subsidiary as the default selected subsidiary
        if(!this.defaultCatalogData?.subsidiary_id) {
          this.selectedSubsidiary = this.allSubsidiaries.find(s => s.is_main);
          if (this.selectedSubsidiary) {
            this.catalogService.setNewCatalogData({
              subsidiary_id: this.selectedSubsidiary.id,
            } as any);
          }
        } else {
          this.selectedSubsidiary = this.allSubsidiaries.find(s => s.id === this.defaultCatalogData.subsidiary_id);
        }

        // Only set form value if form is initialized
        if (this.basicSettingsForm && this.selectedSubsidiary) {
          this.basicSettingsForm.get('subsidiary_id')?.setValue(this.selectedSubsidiary.id);
        }
      },
      error: (error) => {
        this.subsidiariesLoading = false;
        console.error('Error loading subsidiaries:', error);
      }
    });
  }

  initBasicSettingsForm() {
    this.basicSettingsForm = new FormGroup({
      subsidiary_id: new FormControl(this.defaultCatalogData?.subsidiary_id || null, []),
      name: new FormControl(this.defaultCatalogData?.name || '', [Validators.required]),
      description: new FormControl(this.defaultCatalogData?.description || ''),
      cover: new FormControl(null),
      hasExpirationDate: new FormControl(this.defaultCatalogData && this.defaultCatalogData?.expiration_date !== null && this.defaultCatalogData?.expiration_date !== '' ? true : false),
      expiration_date: new FormControl(this.defaultCatalogData?.expiration_date || ''),
    });
    
    // Handle subsidiary change when the form is initialized
    this.handleSubsidiaryChange();
    
    this.basicSettingsForm.valueChanges.subscribe((value) => {
      this.isAllFormsValid.emit(this.basicSettingsForm.valid);
      // Update selectedSubsidiary when form control value changes
      // Skip if we're in the middle of a confirmation dialog
      if (!this.isSubsidaryChangeInProgress) {
        if (value.subsidiary_id) {
          this.selectedSubsidiary = this.allSubsidiaries.find(s => s.id === value.subsidiary_id);
        }
        // Update previous value when change is confirmed
        this.previousSubsidaryId = value.subsidiary_id ?? null;
        this.catalogService.setNewCatalogData({
          ...value,
        });
      }
    });
  }

  initPricingRulesForm() {
    const isDisabled = this.defaultCatalogData?.status === 'Approved';
    // For margin pricing rule, allow negative values. For discount, min is 0 and max is 100
    const pricingRule = this.defaultCatalogData?.pricing_rule || 'discount';
    const overallPricingValidators = pricingRule === 'margin' 
      ? [Validators.required]
      : [Validators.required, Validators.min(0), Validators.max(100)];
    
    this.pricingRulesForm = new FormGroup({
      pricing_rule: new FormControl(
        { value: pricingRule, disabled: isDisabled },
        [Validators.required]
      ),
      overall_pricing_value: new FormControl(this.defaultCatalogData?.overall_pricing_value || 0, overallPricingValidators),
      extra_discount: new FormControl(this.defaultCatalogData?.extra_discount || null),
      extra_discount_blocking: new FormControl(this.defaultCatalogData?.extra_discount_blocking),
    });

    // Update validators when pricing rule changes
    this.pricingRulesForm.get('pricing_rule')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pricingRule) => {
      const overallPricingControl = this.pricingRulesForm.get('overall_pricing_value');
      if (overallPricingControl) {
        const newValidators = pricingRule === 'margin' 
          ? [Validators.required]
          : [Validators.required, Validators.min(0), Validators.max(100)];
        overallPricingControl.setValidators(newValidators);
        overallPricingControl.updateValueAndValidity();
      }
      
      const updatedProducts = this.catalogService.getNewCatalogData()?.products || [];
      const overallPricingValue = this.pricingRulesForm.get('overall_pricing_value')?.value ?? 0;
      
      const productsWithUpdatedPricing = updatedProducts.map((product: IProduct) => {
        const updatedProduct = { ...product };
        
        (updatedProduct as any).hasCustomDiscount = false;
        updatedProduct.discount = overallPricingValue;
        
        if (this.catalogType === 'agents') {
          if (pricingRule === 'margin') {
            const cost = product.cost ?? 0;
            if (cost && cost > 0) {
              updatedProduct.final_price = +(cost * (1 + overallPricingValue / 100)).toFixed(2);
            } else if (product.final_price && product.final_price > 0) {
              updatedProduct.final_price = product.final_price;
            }
          } else {
            const salesPrice = product.sales_price || 0;
            if (salesPrice > 0) {
              updatedProduct.final_price = +(salesPrice * (1 - overallPricingValue / 100)).toFixed(2);
            } else if (product.final_price && product.final_price > 0) {
              updatedProduct.final_price = product.final_price;
            }
          }
        } else {
          const selectedCurrencyId = this.defaultCatalogData?.default_currency || this.currenciesForm?.get('default_currency')?.value;
          updatedProduct.currencies = product.currencies?.map((currency: IProductCurrency) => {
            if (pricingRule === 'margin') {
              if (currency.currency_id === selectedCurrencyId) {
                const cost = currency.cost ?? product.cost ?? 0;
                if (cost && cost > 0) {
                  const finalPrice = +(cost * (1 + overallPricingValue / 100)).toFixed(2);
                  return { ...currency, final_price: finalPrice };
                }
              }
              return { ...currency };
            } else {
              const salesPrice = currency.sales_price || 0;
              if (salesPrice > 0) {
                const finalPrice = +(salesPrice * (1 - overallPricingValue / 100)).toFixed(2);
                return { ...currency, final_price: finalPrice };
              }
              return { ...currency };
            }
          });
        }
        
        return updatedProduct;
      });
      
      this.catalogService.setNewCatalogData({
        pricing_rule: pricingRule,
        products: productsWithUpdatedPricing,
      });
    });

    this.pricingRulesForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.isAllFormsValid.emit(this.pricingRulesForm.valid);
      const updatedProducts = this.catalogService.getNewCatalogData()?.products || [];
      const { overall_pricing_value, extra_discount, pricing_rule } = value;

      const currentPricingRule = pricing_rule || this.defaultCatalogData?.pricing_rule || 'discount';
      
      // Allow negative values for margin, but require >= 0 for discount
      const isValidPricingValue = currentPricingRule === 'margin' 
        ? overall_pricing_value !== null && overall_pricing_value !== undefined
        : overall_pricing_value >= 0;
      
      if (isValidPricingValue || extra_discount >= 0) {
        const productsWithUpdatedPricing = updatedProducts.map((product: IProduct) => {
          const updatedProduct = { ...product };

          if (overall_pricing_value !== null && overall_pricing_value !== undefined) {
            const selectedCurrencyId = this.defaultCatalogData?.default_currency || this.currenciesForm?.get('default_currency')?.value;
            
            // Check if product has cost (for margin mode)
            const hasCost = currentPricingRule === 'margin' 
              ? (product.currencies?.find(c => c.currency_id === selectedCurrencyId)?.cost ?? product.cost ?? 0) > 0
              : true;
            
            // For products without cost in margin mode, keep existing final_price and don't update discount
            if (currentPricingRule === 'margin' && !hasCost) {
              return updatedProduct;
            }
            
            updatedProduct.discount = overall_pricing_value;
            (updatedProduct as any).hasCustomDiscount = false;

            if (this.catalogType === 'agents') {
              const selectedCurrencyId = this.defaultCatalogData?.default_currency || this.currenciesForm?.get('default_currency')?.value;
              updatedProduct.currencies = product.currencies?.map((currency: IProductCurrency) => {
                if (currentPricingRule === 'margin') {
                  const cost = currency.cost ?? product.cost ?? 0;
                  if (cost && cost > 0) {
                    const finalPrice = +(cost * (1 + overall_pricing_value / 100)).toFixed(2);
                    return {
                      ...currency,
                      final_price: finalPrice
                    };
                  } else {
                    return { ...currency };
                  }
                } else {
                  const salesPrice = currency.sales_price ?? product.sales_price ?? 0;
                  if (salesPrice > 0) {
                    const finalPrice = +(salesPrice * (1 - overall_pricing_value / 100)).toFixed(2);
                    return {
                      ...currency,
                      final_price: finalPrice
                    };
                  } else {
                    return { ...currency };
                  }
                }
              });
              
              const defaultCurrency = updatedProduct.currencies?.find(c => c.currency_id === selectedCurrencyId);
              if (defaultCurrency?.final_price !== undefined && defaultCurrency?.final_price !== null) {
                updatedProduct.final_price = Number(defaultCurrency.final_price);
              } else if (currentPricingRule === 'margin') {
                const cost = product.cost ?? 0;
                if (cost && cost > 0) {
                  updatedProduct.final_price = +(cost * (1 + overall_pricing_value / 100)).toFixed(2);
                } else if (product.final_price && product.final_price > 0) {
                  updatedProduct.final_price = product.final_price;
                }
              } else {
                const salesPrice = product.sales_price || 0;
                if (salesPrice > 0) {
                  updatedProduct.final_price = +(salesPrice * (1 - overall_pricing_value / 100)).toFixed(2);
                } else if (product.final_price && product.final_price > 0) {
                  updatedProduct.final_price = product.final_price;
                }
              }
            } else {

              updatedProduct.currencies = product.currencies?.map((currency: IProductCurrency) => {
              // Method: Margin on top of Cost (margin_on_cost)
              // Formula: final_price = cost * (1 + margin / 100) - INCREASES price
              if (currentPricingRule === 'margin') {
                if (currency.currency_id === selectedCurrencyId) {
                  // Update only the selected currency
                  const cost = currency.cost ?? product.cost ?? 0;
                  if (cost && cost > 0) {
                    // Calculate: final_price = cost * (1 + margin / 100)
                    const finalPrice = +(cost * (1 + overall_pricing_value / 100)).toFixed(2);
                    return {
                      ...currency,
                      final_price: finalPrice
                    };
                  } else {
                    return { ...currency };
                  }
                } else {
                  return { ...currency };
                }
              } 
              // Method: Discount on MSRP (discount_on_msrp)
              // Formula: final_price = sales_price * (1 - discount / 100) - DECREASES price
              else {
                const salesPrice = currency.sales_price || 0;
                if (salesPrice > 0) {
                  const finalPrice = +(salesPrice * (1 - overall_pricing_value / 100)).toFixed(2);
                  return {
                    ...currency,
                    final_price: finalPrice
                  };
                } else {
                  return { ...currency };
                }
              }
            });
            }
          }

          if (extra_discount >= 0) {
            updatedProduct.extra_discount = extra_discount;
          }

          return updatedProduct;
        });

        this.catalogService.setNewCatalogData({
          ...value,
          pricing_rule: currentPricingRule,
          products: productsWithUpdatedPricing,
        });
      }
    });
  }

  initCurrenciesForm() {
    this.currenciesForm = new FormGroup({
      default_currency: new FormControl(this.defaultCatalogData?.default_currency ?? this.defaultCurrency?.id ?? null, [Validators.required]),
      enabled_currencies: new FormControl(((this.defaultCatalogData as any)?.enabled_currencies as string[]) || []),
      is_multi_currency: new FormControl(this.defaultCatalogData?.is_multi_currency ?? false),
      show_full_pricing_data: new FormControl(this.defaultCatalogData?.show_full_pricing_data ?? true),
    });

    // Track previous currency values to detect changes
    let previousDefaultCurrency = this.currenciesForm.get('default_currency')?.value;
    let previousIsMultiCurrency = this.currenciesForm.get('is_multi_currency')?.value;

    this.currenciesForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.isAllFormsValid.emit(this.currenciesForm.valid);
      
      // Check if default currency or multi-currency changed
      const defaultCurrencyChanged = previousDefaultCurrency !== value.default_currency;
      const multiCurrencyChanged = previousIsMultiCurrency !== value.is_multi_currency;
      
      // Recalculate final prices for margin mode if currency changed
      if ((defaultCurrencyChanged || multiCurrencyChanged) && this.catalogType === 'catalogs') {
        this.recalculateFinalPricesForCurrencyChange(value.default_currency);
      }
      
      previousDefaultCurrency = value.default_currency;
      previousIsMultiCurrency = value.is_multi_currency;
      
      this.catalogService.setNewCatalogData({
        ...value,
      });
    });

    // Ensure multi-currency cannot be enabled without a default currency
    this.currenciesForm.get('default_currency')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {        
        const isValidDefault = val !== null && val !== undefined && val !== '';
        if (!isValidDefault) {
          // turn off multi-currency and clear enabled currencies when default currency is not set
          this.isMultiCurrency = false;
          this.currenciesForm.get('is_multi_currency')?.setValue(false, { emitEvent: false });
          this.currenciesForm.get('enabled_currencies')?.setValue([], { emitEvent: false });
          this.catalogService.setNewCatalogData({ is_multi_currency: false, enabled_currencies: [] } as any);
        }
      });
  }

  recalculateFinalPricesForCurrencyChange(newCurrencyId: number | null): void {
    if (!newCurrencyId) return;
    
    const catalogData = this.catalogService.getNewCatalogData();
    const pricingRule = catalogData?.pricing_rule || this.defaultCatalogData?.pricing_rule || 'discount';
    const overallPricingValue = catalogData?.overall_pricing_value ?? this.defaultCatalogData?.overall_pricing_value ?? 0;
    
    if (pricingRule !== 'margin') return;
    const products = catalogData?.products || [];
    const updatedProducts = products.map((product: IProduct) => {
    const updatedProduct = { ...product };
      
      if ((product as any).hasCustomDiscount) {
        const customDiscount = product.discount ?? overallPricingValue;
        updatedProduct.currencies = product.currencies?.map((currency: IProductCurrency) => {
          if (currency.currency_id === newCurrencyId) {
            const cost = currency.cost ?? product.cost ?? 0;
            if (cost && cost > 0) {
              const finalPrice = +(cost * (1 + customDiscount / 100)).toFixed(2);
              return {
                ...currency,
                final_price: finalPrice
              };
            }
          }
          return currency;
        });
      } else {
        updatedProduct.discount = overallPricingValue;
        updatedProduct.currencies = product.currencies?.map((currency: IProductCurrency) => {
          if (currency.currency_id === newCurrencyId) {
            const cost = currency.cost ?? product.cost ?? 0;
            if (cost && cost > 0) {
              const finalPrice = +(cost * (1 + overallPricingValue / 100)).toFixed(2);
              return {
                ...currency,
                final_price: finalPrice
              };
            }
          }
          return currency;
        });
      }

      const newCurrency = updatedProduct.currencies?.find(c => c.currency_id === newCurrencyId);
      if (newCurrency?.final_price) {
        updatedProduct.final_price = newCurrency.final_price;
      }
      
      return updatedProduct;
    });
    
    this.catalogService.setNewCatalogData({
      products: updatedProducts,
    });
  }

  initOrderAmountForm() {
    this.orderAmountForm = new FormGroup({
      min_order_amount: new FormControl(this.defaultCatalogData?.min_order_amount || null, []),
    });

    this.orderAmountForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.catalogService.setNewCatalogData({
        ...value,
      });
    });
  }

  initQuantityRulesForm() {
    this.quantityRulesForm = new FormGroup({
      increment: new FormControl(this.defaultCatalogData?.quantity_rules?.increment ?? 1, [Validators.required]),
      max_qte: new FormControl(this.defaultCatalogData?.quantity_rules?.max_qte ?? null),
      min_qte: new FormControl(this.defaultCatalogData?.quantity_rules?.min_qte ?? 1, [Validators.required]),
      can_be_sold_out_of_stock: new FormControl(this.defaultCatalogData?.quantity_rules?.can_be_sold_out_of_stock ?? false, [Validators.required]),
      show_stock: new FormControl(this.defaultCatalogData?.quantity_rules?.show_stock ?? true, [Validators.required]),
    }, { validators: customValidator(this.translateService) });

    this.quantityRulesForm.get('increment')?.valueChanges.subscribe((value: number) => {
      this.updateCatalogProductsRules({increment: value});
    })
    this.quantityRulesForm.get('min_qte')?.valueChanges.subscribe((value: number) => {
      this.updateCatalogProductsRules({min_qte: value});
    })
    this.quantityRulesForm.get('max_qte')?.valueChanges.subscribe((value: number | null) => {
      this.updateCatalogProductsRules({max_qte: value});
    })

    this.quantityRulesForm.get('show_stock')?.valueChanges.subscribe((value: boolean) => {
      if (value === false) this.quantityRulesForm.get('can_be_sold_out_of_stock')?.setValue(true);
    })
    this.quantityRulesForm.get('can_be_sold_out_of_stock')?.valueChanges.subscribe((value: boolean) => {
      this.updateCatalogProductsRules({can_be_sold_out_of_stock: value});
      if (value === false) this.quantityRulesForm.get('show_stock')?.setValue(true);
    })

    this.quantityRulesForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.isAllFormsValid.emit(this.quantityRulesForm.valid);
      this.catalogService.setNewCatalogData({
        quantity_rules: value,
      });
    });
  }

  handleSubsidiaryChange() {
    if(!this.basicSettingsForm) return;

    // Initialize previous value
    this.previousSubsidaryId = this.selectedSubsidiary?.id ?? null;

    // Subscribe to subsidiary_id changes specifically
    this.basicSettingsForm.get('subsidiary_id')?.valueChanges.pipe(
      skip(1), // Skip the first change done by loadSubsidaries() to initialize the selected subsidiary
      takeUntil(this.destroy$),
      distinctUntilChanged()
    ).subscribe((newValue) => {
      // Skip if we're in the middle of a confirmation dialog
      if (this.isSubsidaryChangeInProgress) {
        return;
      }

      const previousValue = this.previousSubsidaryId;
      
      // If the value hasn't actually changed, do nothing
      if (newValue === previousValue) {
        return;
      }

      // Prevent form valueChanges from processing this change temporarily
      this.isSubsidaryChangeInProgress = true;
      
      // Temporarily update selectedSubsidiary to show the new value in UI
      // The form control already has the new value from mat-select
      if (newValue) {
        this.selectedSubsidiary = this.allSubsidiaries.find(s => s.id === newValue);
      }
      
      // Show confirmation dialog
      const dialogRef = this.dialog.open(SubsidiaryChangeConfirmationDialogComponent, {
        data: {
          catalogType: this.catalogType,
        },
      });

      dialogRef.afterClosed().subscribe((result: boolean) => {
        if (!result) {
          // If cancelled, revert to the previous value
          this.basicSettingsForm.get('subsidiary_id')?.setValue(previousValue, { emitEvent: true });
          this.selectedSubsidiary = previousValue 
            ? this.allSubsidiaries.find(s => s.id === previousValue)
            : undefined;
        } else {
          // If confirmed, keep the new value and update previous value for next change
          this.previousSubsidaryId = newValue;
        }
        
        // Allow form valueChanges to process changes again
        this.isSubsidaryChangeInProgress = false;
        
        // If confirmed, trigger valueChanges to process the new value
        if (result) {
          // The value is already set, so we need to manually trigger the update
          // by setting it again with emitEvent: true, or by manually calling the update logic

          const currentFormValue = this.basicSettingsForm.value;

          if (this.catalogType === 'catalogs') {
            // Filter the customers to only include the ones that belong to the selected subsidiary
          const newCustomers = this.catalogService.getNewCatalogData()?.customers.filter((customer: ICustomer) => customer.subsidiary_company_ids!.includes(newValue) || customer.subsidiary_company_ids!.length === 0);
          const newLocations = newCustomers?.flatMap((customer: ICustomer) => customer.locations!).map((location: ILocation) => location.id) || [];

          this.catalogService.setNewCatalogData({
            ...currentFormValue,
            customers: newCustomers,
            locations_ids: newLocations,
          });
          } else if (this.catalogType === 'agents') {
            // Filter the agents to only include the ones that belong to the selected subsidiary
            const newAgents = this.catalogService.getNewCatalogData()?.agents.filter((agent: IAgent) => agent.subsidiary_agent_ids && (agent.subsidiary_agent_ids.includes(newValue) || agent.subsidiary_agent_ids.length === 0));
            this.catalogService.setNewCatalogData({
              ...currentFormValue,
              agents: newAgents,
            });
            
            // Refresh the agents list for the selected subsidiary
            this.store.dispatch(GetAgents({subsidiary_id: newValue}));
          }
        }
      });
    });
  }

  updateCatalogProductsRules(values: Partial<IGeneralRules>) {
    if (!this.canSubmit) return;
    const products = this.catalogService.getNewCatalogData()?.products.map((product: IProduct) => {
      return {
        ...product,
        ...values,
      }
    })
    console.log('New products:', products);
    this.catalogService.setNewCatalogData({
      ...values,
      products
    });
  }

  toggleExpirationDate(event: any) {
    if (!this.canSubmit) return;
    const checkbox = event.checked
    
    if (checkbox) {
      this.basicSettingsForm.get('hasExpirationDate')?.setValue(true);
      this.basicSettingsForm.get('expiration_date')?.setValue(this.defaultCatalogData.expiration_date);
      this.basicSettingsForm
        .get('expiration_date')
        ?.setValidators([Validators.required]);
    } else {
      this.basicSettingsForm.get('hasExpirationDate')?.setValue(false);
      this.basicSettingsForm.get('expiration_date')?.clearValidators();
      this.basicSettingsForm.get('expiration_date')?.setValue('');
      // Reset the touched and pristine states to hide validation messages
      this.basicSettingsForm.get('expiration_date')?.markAsUntouched();
      this.basicSettingsForm.get('expiration_date')?.markAsPristine();
    }
    this.basicSettingsForm.get('expiration_date')?.updateValueAndValidity();
    this.basicSettingsForm.get('hasExpirationDate')?.updateValueAndValidity();
  }

  toggleExtraDiscount() {
    if (!this.canSubmit) return;
    this.hasExtraDiscount = !this.hasExtraDiscount;
    this.catalogService.setNewCatalogData({has_extra_discount: this.hasExtraDiscount})
    if (this.hasExtraDiscount) {
      this.pricingRulesForm.get('extra_discount')?.setValue(0);
      this.pricingRulesForm
        .get('extra_discount')
        ?.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
    } else {
      this.pricingRulesForm.get('extra_discount')?.setValue(null);
      this.pricingRulesForm.get('extra_discount_blocking')?.setValue(false);
      this.pricingRulesForm.get('extra_discount')?.clearValidators();
      this.pricingRulesForm.get('extra_discount')?.markAsUntouched();
      this.pricingRulesForm.get('extra_discount')?.markAsPristine();
    }
    this.pricingRulesForm.get('extra_discount')?.updateValueAndValidity();
  }

  toggleMultiCurrency() {
    if (!this.canSubmit) return;
    if (this.currenciesForm?.get('default_currency')?.invalid) {
      this.currenciesForm?.get('default_currency')?.markAsTouched();
      this.currenciesForm?.get('default_currency')?.updateValueAndValidity();
      return;
    }
    
    this.isMultiCurrency = !this.isMultiCurrency;
    this.currenciesForm?.get('is_multi_currency')?.setValue(this.isMultiCurrency);

    if (this.isMultiCurrency) {
      const baseCurrency = this.currenciesForm?.get('default_currency')?.value;
      this.currenciesForm?.get('enabled_currencies')?.setValue(baseCurrency != null ? [baseCurrency] : []);
    } else {
      this.currenciesForm?.get('enabled_currencies')?.setValue([]);
    }
    
    // Recalculate final prices for margin mode when multi-currency is toggled
    if (this.catalogType === 'catalogs') {
      const defaultCurrency = this.currenciesForm?.get('default_currency')?.value;
      if (defaultCurrency) {
        this.recalculateFinalPricesForCurrencyChange(defaultCurrency);
      }
    }
    
    this.catalogService.setNewCatalogData({ is_multi_currency: this.isMultiCurrency } as any);
  }

  toggleShowFullPricingData() {
    if (!this.canSubmit) return;
    const current = this.currenciesForm?.get('show_full_pricing_data')?.value === true;
    this.currenciesForm?.get('show_full_pricing_data')?.setValue(!current);
    this.catalogService.setNewCatalogData({ show_full_pricing_data: !current } as any);
  }

  onFileSelected(event: Event) {
    if (!this.canSubmit) return;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const dataUrl = e.target.result
        const base64Image = dataUrl.split(',')[1];

        this.catalogService.setNewCatalogData({coverUrl: base64Image})

        this.imagePreview = reader.result as string;
        this.imageLoadError = false; // Reset error flag when new image is loaded
        this.basicSettingsForm.get('cover')?.setValue(file);
      };
      reader.readAsDataURL(file);
    }
  }

  onInputRemoveLeadingZero(event: Event, formGroup: FormGroup, formControlName: string) {
    if (!this.canSubmit) return;
    const input = event.target as HTMLInputElement;
    if (/^0[0-9]+/.test(input.value)) {
      input.value = input.value.replace(/^0+/, '');
      formGroup.get(formControlName)?.setValue(input.value);
    }
  }

  onImageError(event: Event) {
    this.imageLoadError = true;
  }

  removeImage() {
    if (!this.canSubmit) return;
    this.basicSettingsForm.get('cover')?.reset();
    this.catalogService.setNewCatalogData({ coverUrl: null, cover: new File([], '') })
    this.imagePreview = null;
    this.imageLoadError = false;
    // Optionally, also reset the file input element
    const fileInput = document.getElementById('cover') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onEnabledHandlingUnitsChange(value: number[]): void {
    if (!this.canSubmit) return;
    this.enabledHandlingUnits = value;
    
    // If no default is set and we have enabled units, suggest the first one
    if (!this.defaultHandlingUnit && value.length > 0) {
      this.defaultHandlingUnit = value[0];
      this.catalogService.setNewCatalogData({
        default_handling_unit_id: this.defaultHandlingUnit,
      } as any);
    }
    
    this.catalogService.setNewCatalogData({
      enabled_handling_unit_ids: value,
    } as any);
  }

  onDefaultHandlingUnitChange(value: number): void {
    if (!this.canSubmit) return;
    this.defaultHandlingUnit = value;
    this.catalogService.setNewCatalogData({
      default_handling_unit_id: value,
      products: this.catalogService.getNewCatalogData()?.products.map((product: IProduct) => {
        return {
          ...product,
          handling_units: product.handling_units?.map((hu: any) => {
            return { ...hu, default: hu.type_id === value };
            }) || [],
          }
        }) || [],
    } as any);
  }

  getSelectedEnabledHandlingUnits(): IHandlingUnitType[] {
    if (!this.enabledHandlingUnits || this.enabledHandlingUnits.length === 0) {
      return [];
    }
    return this.availableHandlingUnits.filter(unit => this.enabledHandlingUnits.includes(unit.id))
      .sort((a, b) => (a.quantity || 0) - (b.quantity || 0));
  }

  getEnabledHandlingUnitsLabel(): string {
    if (!this.enabledHandlingUnits || this.enabledHandlingUnits.length === 0) {
      return '';
    }
    return this.enabledHandlingUnits
      .map(id => {
        const unit = this.availableHandlingUnits.find(u => u.id === id);
        return unit ? unit.name : '';
      })
      .filter(name => name)
      .join(', ');
  }
  
  getDefaultHandlingUnitLabel(): string {
    if (!this.defaultHandlingUnit) {
      return '';
    }
    const unit = this.availableHandlingUnits.find(u => u.id === this.defaultHandlingUnit);
    return unit ? unit.name : '';
  }

  getFilteredHandlingUnitsForDefault(): IHandlingUnitType[] {
    if (!this.enabledHandlingUnits || this.enabledHandlingUnits.length === 0) {
      return [];
    }
    return this.availableHandlingUnits.filter(unit => this.enabledHandlingUnits.includes(unit.id))
      .sort((a, b) => (a.quantity || 0) - (b.quantity || 0));
  }

  private buildComparableCatalogSnapshot(data: INewCatalog) {
    return {
      name: data.name || '',
      description: data.description || '',
      expiration_date: data.expiration_date || '',
      pricing_rule: data.pricing_rule || 'discount',
      overall_pricing_value: data.overall_pricing_value ?? 0,
      extra_discount: data.extra_discount ?? null,
      extra_discount_blocking: !!data.extra_discount_blocking,
      min_order_amount: data.min_order_amount ?? null,
      quantity_rules: {
        increment: data.quantity_rules?.increment ?? 1,
        max_qte: data.quantity_rules?.max_qte ?? null,
        min_qte: data.quantity_rules?.min_qte ?? 1,
        can_be_sold_out_of_stock: !!data.quantity_rules?.can_be_sold_out_of_stock,
        show_stock: !!data.quantity_rules?.show_stock,
      },
      has_extra_discount: !!data.has_extra_discount,
      coverUrl: this.imagePreview || data.coverUrl || null,
      default_currency: data.default_currency ?? null,
      enabled_currencies: data.enabled_currencies ?? [],
      is_multi_currency: data.is_multi_currency ?? false,
      show_full_pricing_data: data.show_full_pricing_data ?? true,
      enabled_handling_unit_ids: (data as any).enabled_handling_unit_ids ?? [],
      default_handling_unit_id: (data as any).default_handling_unit_id ?? null,
      catalogtId: data.catalogtId ?? null,
    };
  }

  editObjectCustomFields() {
    if (!this.canSubmit) return;
    const dialogRef = this.dialog.open(CatalogCustomFieldsComponent, {
      data: {
        objectCustomFields: this.objectCustomFields,
      }
    });
    dialogRef.afterClosed().subscribe((result: IObjectCustomFields['values']) => {
      if (result) {
        const catalogId = this.catalogService.getNewCatalogData()?.catalogtId;
        if (catalogId) {
          this.store.dispatch(UpdateObjectCustomFields({ params: this.objectCustomFieldsParams, values: result }));
        } else {
          this.objectCustomFields = {
            values: {
              ...(this.objectCustomFields?.values ?? {}),
              ...result,
            },
            definitions: this.objectCustomFields?.definitions ?? [],
          } as IObjectCustomFields;
          this.catalogService.setNewCatalogData({
            ...this.catalogService.getNewCatalogData()!,
            custom_fields: this.objectCustomFields.values,
          });
        }
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isEditMode(): boolean {
    return !!this.defaultCatalogData?.catalogtId;
  }

  get canSubmit(): boolean {
    return this.isEditMode ? this.canEdit : this.canCreate;
  }

  submitPermission(): HasPermissionContext {
    return { module: 'catalog', action: this.isEditMode ? 'edit' : 'create' };
  }

  private applyReadOnlyState() {
    if (!this.basicSettingsForm || !this.pricingRulesForm || !this.currenciesForm || !this.orderAmountForm || !this.quantityRulesForm) {
      return;
    }
    if (this.canSubmit) {
      this.basicSettingsForm.enable({ emitEvent: false });
      this.pricingRulesForm.enable({ emitEvent: false });
      this.currenciesForm.enable({ emitEvent: false });
      this.orderAmountForm.enable({ emitEvent: false });
      this.quantityRulesForm.enable({ emitEvent: false });
      if (this.isPricingRuleDisabled) {
        this.pricingRulesForm.get('pricing_rule')?.disable({ emitEvent: false });
      }
    } else {
      this.basicSettingsForm.disable({ emitEvent: false });
      this.pricingRulesForm.disable({ emitEvent: false });
      this.currenciesForm.disable({ emitEvent: false });
      this.orderAmountForm.disable({ emitEvent: false });
      this.quantityRulesForm.disable({ emitEvent: false });
    }
  }
}
